import { TerrainSurfaceRegionRef } from '../SceneManifest';

/** World-space AABB of a tile. */
export interface TileBoundsXZ {
  worldMinX: number;
  worldMinZ: number;
  worldMaxX: number;
  worldMaxZ: number;
}

/**
 * Rasterizes a list of polygon surface regions into per-tile index + weight maps.
 *
 * **Two-buffer output** (see {@link RasterizedSurfaceMap}):
 *   - `indices`: RG-packed Uint8Array (2 bytes per texel) — R = layerA, G = layerB.
 *     Uploaded with NEAREST filtering so integer indices stay crisp.
 *   - `weight`: Uint8Array (1 byte per texel) — blend toward B, 0 = fully A, 255 = fully B.
 *     Uploaded with LINEAR filtering so the blend gradient smooths bilinearly
 *     across the texel grid (the visible "blocky boundary" artifact without
 *     LINEAR weight sampling).
 *
 * Algorithm:
 *  1. Fill the buffer with the default surface (R=G=defaultLayer, weight=0).
 *  2. Process regions sorted by priority ascending (painter's algorithm: lower
 *     priority first so higher priority regions paint on top).
 *  3. For each region, compute a 2D polygon SDF per texel centre. Within the
 *     symmetric feather band `[-feather, +feather]`, the weight ramps linearly
 *     from 255 (outside, fully old) to 0 (inside, fully new) with 127 exactly
 *     at the polygon edge.
 *
 * The texel resolution defaults to TEXELS_PER_METER texels per world meter.
 * For a standard 250 m tile at 0.5 tex/m this yields a 128×128 map.
 */
export const TEXELS_PER_METER = 0.5; // 2 m per texel

export interface RasterizedSurfaceMap {
  /** RG packed (2 bytes/texel): R=layerA, G=layerB. NEAREST upload. */
  indices: Uint8Array;
  /** Single-channel weight (1 byte/texel): 0 = fully A, 255 = fully B. LINEAR upload. */
  weight: Uint8Array;
  width: number;
  height: number;
  /**
   * Every surfaceId that ended up contributing to this tile — the default
   * surface (when authored) plus every region whose layer was resolved. Used
   * by `TiledTerrainStreamer` to acquire / release per-surface textures
   * through `SurfaceTextureManager` as tiles enter and leave the streaming ring.
   *
   * The set may include an empty string (the placeholder layer-0 entry); the
   * manager treats that as a no-op since it always maps to layer 0.
   */
  surfaceIds: Set<string>;
}

/** Compute the index map pixel dimensions for a tile. */
export function indexMapSize(
  tileBounds: TileBoundsXZ,
  texelsPerMeter: number = TEXELS_PER_METER,
): { width: number; height: number } {
  return {
    width: Math.max(1, Math.round((tileBounds.worldMaxX - tileBounds.worldMinX) * texelsPerMeter)),
    height: Math.max(1, Math.round((tileBounds.worldMaxZ - tileBounds.worldMinZ) * texelsPerMeter)),
  };
}

export function rasterizeTile(
  tileBounds: TileBoundsXZ,
  regions: TerrainSurfaceRegionRef[],
  surfaceLayerMap: Map<string, number>,
  defaultLayerIndex: number,
  texelsPerMeter: number = TEXELS_PER_METER,
  /**
   * The surfaceId of the default layer (layer 0). When non-null, included in the
   * result's `surfaceIds` so the streamer fetches its textures even on tiles with
   * no region overrides. Pass `null` for the unauthored placeholder slot.
   */
  defaultSurfaceId: string | null = null,
): RasterizedSurfaceMap {
  const { width, height } = indexMapSize(tileBounds, texelsPerMeter);
  const texelCount = width * height;
  const surfaceIds = new Set<string>();
  if (defaultSurfaceId && defaultSurfaceId.length > 0) {
    surfaceIds.add(defaultSurfaceId);
  }

  // Separate buffers: indices (RG, 2 bytes/texel) + weight (R, 1 byte/texel).
  // Decoupling lets the GL layer set NEAREST on indices (integer correctness)
  // and LINEAR on weight (smooth gradient at boundaries).
  const indices = new Uint8Array(texelCount * 2);
  const weight = new Uint8Array(texelCount);

  // Fill default: indices=(default, default). Weight=255 (== "fully B"),
  // which since A=B is visually identical to weight=0 BUT makes bilinear
  // sampling continuous across the outer feather edge:
  //   - Outer-feather texel: (newLayer, default, 255)         → renders default
  //   - Outside texel:        (default,  default, 255)         → renders default
  // Both have weight=255, so the bilinear midpoint stays at 255 even where
  // NEAREST indices flip from (newLayer, default) to (default, default).
  // With weight=0 outside (the previous default), the midpoint dropped to ~127
  // and the (newLayer, default) side rendered as a 50% blend — a visible
  // bright streak at the polygon's outer feather edge.
  const defLayer = Math.max(0, Math.min(255, defaultLayerIndex));
  for (let i = 0; i < texelCount; i++) {
    indices[i * 2 + 0] = defLayer;
    indices[i * 2 + 1] = defLayer;
    weight[i] = 255;
  }

  if (regions.length === 0) return { indices, weight, width, height, surfaceIds };

  // Sort by priority ascending so high-priority regions paint last (painter's algorithm).
  const sorted = [...regions].sort((a, b) => a.priority - b.priority);

  const tileW = tileBounds.worldMaxX - tileBounds.worldMinX;
  const tileD = tileBounds.worldMaxZ - tileBounds.worldMinZ;
  const cellW = tileW / width;
  const cellD = tileD / height;

  for (const region of sorted) {
    const layerIndex = surfaceLayerMap.get(region.surfaceId);
    if (layerIndex === undefined) continue;

    const poly = region.vertices; // [x, z][]
    if (poly.length < 3) continue;

    surfaceIds.add(region.surfaceId);

    const newLayer = Math.max(0, Math.min(255, layerIndex));
    const feather = region.featherDistance;

    // Compute axis-aligned bounding box of the polygon to skip texels clearly outside.
    let polyMinX = poly[0][0];
    let polyMaxX = poly[0][0];
    let polyMinZ = poly[0][1];
    let polyMaxZ = poly[0][1];
    for (let v = 1; v < poly.length; v++) {
      if (poly[v][0] < polyMinX) polyMinX = poly[v][0];
      if (poly[v][0] > polyMaxX) polyMaxX = poly[v][0];
      if (poly[v][1] < polyMinZ) polyMinZ = poly[v][1];
      if (poly[v][1] > polyMaxZ) polyMaxZ = poly[v][1];
    }

    // Expand AABB by feather for the blend zone.
    const testMinX = polyMinX - feather;
    const testMaxX = polyMaxX + feather;
    const testMinZ = polyMinZ - feather;
    const testMaxZ = polyMaxZ + feather;

    for (let row = 0; row < height; row++) {
      const wz = tileBounds.worldMinZ + (row + 0.5) * cellD;
      if (wz < testMinZ || wz > testMaxZ) continue;

      for (let col = 0; col < width; col++) {
        const wx = tileBounds.worldMinX + (col + 0.5) * cellW;
        if (wx < testMinX || wx > testMaxX) continue;

        const sdf = polygonSdf(wx, wz, poly);
        const texelIdx = row * width + col;
        const idxOff = texelIdx * 2;

        if (feather > 0) {
          // Symmetric feather: monotonic ramp across the band [-feather, +feather].
          // weight encodes blend toward B (the previous/old surface):
          //   sdf = -feather → weight = 255 (fully old, equivalent to not writing)
          //   sdf = 0        → weight = 127 (50% blend exactly at polygon boundary)
          //   sdf = +feather → weight = 0   (fully new)
          // Earlier code split inside / outside into separate branches that
          // disagreed at sdf=0 — produced "alternating fully-blended cells" at
          // typical texel sizes. The continuous ramp here removes that.
          if (sdf <= -feather) continue;
          const prevA = indices[idxOff + 0];
          if (sdf >= feather) {
            indices[idxOff + 0] = newLayer;
            indices[idxOff + 1] = newLayer;
            weight[texelIdx] = 0;
          } else {
            const t = (feather - sdf) / (2 * feather);
            const w = Math.round(Math.max(0, Math.min(1, t)) * 255);
            indices[idxOff + 0] = newLayer;
            indices[idxOff + 1] = prevA;
            weight[texelIdx] = w;
          }
        } else {
          // feather == 0: hard edge, no blending. Only inside the polygon.
          if (sdf < 0) continue;
          indices[idxOff + 0] = newLayer;
          indices[idxOff + 1] = newLayer;
          weight[texelIdx] = 0;
        }
      }
    }
  }

  return { indices, weight, width, height, surfaceIds };
}

/**
 * 2D signed distance to a closed polygon. Positive = inside, negative = outside.
 * Uses the winding-number sign convention (XZ plane, Y-up world).
 *
 * The SDF value is the distance to the nearest edge, signed by whether the point
 * is inside the polygon. Algorithm: minimum perpendicular distance to each segment,
 * with inside/outside determined by ray casting.
 */
function polygonSdf(px: number, pz: number, poly: number[][]): number {
  const n = poly.length;
  let minDist = Infinity;
  let inside = false;

  for (let i = 0, j = n - 1; i < n; j = i++) {
    const ax = poly[j][0];
    const az = poly[j][1];
    const bx = poly[i][0];
    const bz = poly[i][1];

    // Minimum distance from point to segment [a, b].
    const dx = bx - ax;
    const dz = bz - az;
    const len2 = dx * dx + dz * dz;
    let dist: number;
    if (len2 < 1e-12) {
      const ex = px - ax;
      const ez = pz - az;
      dist = Math.sqrt(ex * ex + ez * ez);
    } else {
      const t = Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / len2));
      const cx = ax + t * dx;
      const cz = az + t * dz;
      const ex = px - cx;
      const ez = pz - cz;
      dist = Math.sqrt(ex * ex + ez * ez);
    }
    if (dist < minDist) minDist = dist;

    // Ray-casting for inside test (along +X).
    if ((az > pz) !== (bz > pz) && px < ax + ((pz - az) / (bz - az)) * (bx - ax)) {
      inside = !inside;
    }
  }

  return inside ? minDist : -minDist;
}

/** Returns true if the polygon's AABB overlaps the tile bounds (expanded by feather). */
export function regionOverlapsTile(
  region: TerrainSurfaceRegionRef,
  tileBounds: TileBoundsXZ,
): boolean {
  const aabb = regionAabb(region);
  if (!aabb) return false;
  return (
    aabb.maxX >= tileBounds.worldMinX &&
    aabb.minX <= tileBounds.worldMaxX &&
    aabb.maxZ >= tileBounds.worldMinZ &&
    aabb.minZ <= tileBounds.worldMaxZ
  );
}

/** Polygon AABB expanded by featherDistance. Null for empty polygons. */
export interface RegionAabb {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

export function regionAabb(region: TerrainSurfaceRegionRef): RegionAabb | null {
  const poly = region.vertices;
  if (poly.length === 0) return null;
  let minX = poly[0][0];
  let maxX = poly[0][0];
  let minZ = poly[0][1];
  let maxZ = poly[0][1];
  for (let v = 1; v < poly.length; v++) {
    const x = poly[v][0];
    const z = poly[v][1];
    if (x < minX) minX = x;
    else if (x > maxX) maxX = x;
    if (z < minZ) minZ = z;
    else if (z > maxZ) maxZ = z;
  }
  const f = region.featherDistance;
  return { minX: minX - f, maxX: maxX + f, minZ: minZ - f, maxZ: maxZ + f };
}

/** Whether two AABBs overlap on XZ. */
export function aabbOverlapsTile(aabb: RegionAabb, tile: TileBoundsXZ): boolean {
  return (
    aabb.maxX >= tile.worldMinX &&
    aabb.minX <= tile.worldMaxX &&
    aabb.maxZ >= tile.worldMinZ &&
    aabb.minZ <= tile.worldMaxZ
  );
}

/**
 * Semantic equality for two surface regions — true if they would rasterize
 * identically. Used by streamers to detect which regions actually changed
 * between updates and skip tiles those regions can't have affected.
 */
export function regionsEqual(a: TerrainSurfaceRegionRef, b: TerrainSurfaceRegionRef): boolean {
  if (a.surfaceId !== b.surfaceId) return false;
  if (a.priority !== b.priority) return false;
  if (a.featherDistance !== b.featherDistance) return false;
  if (a.vertices.length !== b.vertices.length) return false;
  for (let i = 0; i < a.vertices.length; i++) {
    if (a.vertices[i][0] !== b.vertices[i][0]) return false;
    if (a.vertices[i][1] !== b.vertices[i][1]) return false;
  }
  return true;
}

/**
 * Returns the AABBs that changed between two region lists. Used by streamers
 * to identify tiles dirtied by an authoring edit:
 *   - Added region   → new AABB
 *   - Removed region → old AABB (so previously painted texels reset)
 *   - Modified region → BOTH old and new AABB (vertex drag exits the old AABB)
 * Unchanged regions contribute nothing. Returns an empty array when nothing
 * relevant changed.
 */
export function changedRegionAabbs(
  oldRegions: readonly TerrainSurfaceRegionRef[],
  newRegions: readonly TerrainSurfaceRegionRef[],
): RegionAabb[] {
  const oldById = new Map<string, TerrainSurfaceRegionRef>();
  for (const r of oldRegions) oldById.set(r.id, r);
  const dirty: RegionAabb[] = [];
  const seenIds = new Set<string>();
  for (const r of newRegions) {
    seenIds.add(r.id);
    const old = oldById.get(r.id);
    if (!old) {
      const aabb = regionAabb(r);
      if (aabb) dirty.push(aabb);
    } else if (!regionsEqual(old, r)) {
      const oldAabb = regionAabb(old);
      const newAabb = regionAabb(r);
      if (oldAabb) dirty.push(oldAabb);
      if (newAabb) dirty.push(newAabb);
    }
  }
  for (const r of oldRegions) {
    if (seenIds.has(r.id)) continue;
    const aabb = regionAabb(r);
    if (aabb) dirty.push(aabb);
  }
  return dirty;
}
